
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, Zap, TrendingUp, Bot } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { TourButton } from '@/components/common/TourButton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export const EnhancedDashboardHeader = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [avatarFallback, setAvatarFallback] = useState<'logo' | 'initials'>('logo');

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const quickActions = [
    {
      label: isMobile ? t('connect') : t('connect_account'),
      icon: Plus,
      onClick: () => navigate('/exchange-accounts'),
      color: 'bg-primary/90 hover:bg-primary',
      iconColor: 'text-white'
    },
    {
      label: isMobile ? t('bots') : t('my_bots'),
      icon: Bot,
      onClick: () => navigate('/my-bots'),
      color: 'bg-blue-500/90 hover:bg-blue-500',
      iconColor: 'text-white'
    },
    {
      label: isMobile ? t('premium') : t('premium_bots'),
      icon: Zap,
      onClick: () => navigate('/premium-bots'),
      color: 'bg-gradient-to-r from-purple-500/90 to-pink-500/90 hover:from-purple-500 hover:to-pink-500',
      iconColor: 'text-white'
    },
    {
      label: isMobile ? t('prop') : t('prop_trading'),
      icon: TrendingUp,
      onClick: () => navigate('/prop-trading'),
      color: 'bg-green-500/90 hover:bg-green-500',
      iconColor: 'text-white'
    }
  ];

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-muted/20 border border-border/50 rounded-xl p-4 md:p-6 mb-6 md:mb-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-40 h-40 bg-primary rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-blue-500 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2 animate-pulse" />
        <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-purple-400 rounded-full blur-2xl transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
      </div>

      <div className="relative">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar className={`${isMobile ? 'h-12 w-12' : 'h-16 w-16'} border-2 border-primary/20 shadow-lg animate-fade-in`}>
                <AvatarImage src={user?.user_metadata?.avatar_url} />
                <AvatarFallback className="bg-primary/10 text-primary font-bold">
                  {avatarFallback === 'logo' ? (
                    <img
                      src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl/favicon-icon.png"
                      alt="Trade Bot 365"
                      className="w-full h-full object-contain"
                      onError={() => setAvatarFallback('initials')}
                    />
                  ) : (
                    <span className="text-xs font-bold">
                      {user?.user_metadata?.name ? getInitials(user.user_metadata.name) : 'TB'}
                    </span>
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 h-4 w-4 bg-green-500 border-2 border-background rounded-full animate-pulse" />
            </div>
            <div className="animate-fade-in">
              <div className="flex items-center gap-2">
                <h1 className={`${isMobile ? "text-xl" : "text-3xl"} font-bold bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent`}>
                  {t('welcome_back')}, {user?.user_metadata?.name || 'Trader'}!
                </h1>
                <TourButton pageId="dashboard" />
              </div>
              <p className="text-sm md:text-base text-muted-foreground mt-1 animate-fade-in">
                {t('dashboard_summary')}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions with subtle hover effects */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {quickActions.map((action, index) => (
            <Card 
              key={action.label}
              className="group hover:shadow-lg hover:shadow-black/5 transition-all duration-500 hover:-translate-y-1 cursor-pointer border-border/50 bg-gradient-to-br from-background to-muted/10 hover:border-border/80"
              onClick={action.onClick}
            >
              <div className="p-3 md:p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className={`p-2 md:p-3 rounded-lg ${action.color} transition-all duration-300 group-hover:scale-105 shadow-sm hover:shadow-md`}>
                    <action.icon className={`h-4 w-4 md:h-5 md:w-5 ${action.iconColor}`} />
                  </div>
                  <span className="text-xs md:text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-300">
                    {action.label}
                  </span>
                </div>
              </div>
              {/* Subtle glow effect */}
              <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-primary/5 to-transparent" />
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
